import * as React from 'react';
import { useEffect, useState } from 'react';
import { ApiFieldModel, ApiField, getFieldModels, addFieldModel, updateFieldModel, deleteFieldModel, getFieldsByModel, addField, updateField, deleteField } from '@api/index';

interface FieldModelForm { fieldModelName: string; fieldModelDescription: string; fieldModelType: string; }
interface FieldForm { fieldName: string; fieldDataType: string; fieldLength: number; fieldRequired: boolean; fieldTitle: string; fieldOffset: string; fieldShowInSearch: boolean; fieldShowInDataGrid: boolean; fieldIsUniqueFolderIndex: boolean; fieldIsUniqueDocumentIndex: boolean; fieldControlOrderBy: number; }

const emptyModel: FieldModelForm = { fieldModelName: '', fieldModelDescription: '', fieldModelType: '' };
const emptyField: FieldForm = { fieldName: '', fieldDataType: 'X', fieldLength: 0, fieldRequired: false, fieldTitle: '', fieldOffset: '', fieldShowInSearch: false, fieldShowInDataGrid: false, fieldIsUniqueFolderIndex: false, fieldIsUniqueDocumentIndex: false, fieldControlOrderBy: 0 };

// Restrict datatype options to the four supported values and map to backend codes
const dataTypeOptions = [
  { value: 'text', label: 'Text', code: 'X' },
  { value: 'decimal', label: 'Decimal', code: 'D' },
  { value: 'currency', label: 'Currency', code: 'V' },
  { value: 'datetime', label: 'Date/Time', code: 'T' }
];

// Model type options
const modelTypeOptions = [
  { value: 'Document', label: 'Document' },
  { value: 'Folder', label: 'Folder' },
  { value: 'Mixed', label: 'Mixed' }
];

export default function FieldDefinitionsAdmin() {
  const [models, setModels] = useState<ApiFieldModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [modelEditing, setModelEditing] = useState(false);
  const [modelForm, setModelForm] = useState<FieldModelForm>(emptyModel);
  const [fields, setFields] = useState<ApiField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [fieldEditing, setFieldEditing] = useState(false);
  const [fieldForm, setFieldForm] = useState<FieldForm>(emptyField);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { loadModels(); }, []);
  async function loadModels() { setLoading(true); setError(null); try { setModels(await getFieldModels()); } catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function loadFields(modelId:number){ try { setFields(await getFieldsByModel(modelId)); } catch { setFields([]);} }

  function handleSelectModel(id:number){ setSelectedModelId(id); setModelEditing(false); setSelectedFieldId(null); setFieldEditing(false); loadFields(id); const m=models.find(x=>x.id===id); if(m) setModelForm({ fieldModelName:m.fieldModelName, fieldModelDescription:m.fieldModelDescription, fieldModelType:m.fieldModelType }); }
  function handleAddModel(){ setSelectedModelId(null); setModelEditing(true); setModelForm(emptyModel); setFields([]); setSelectedFieldId(null); }
  function handleEditModel(){ if(selectedModelId!=null) setModelEditing(true); }
  async function handleDeleteModel(){ if(selectedModelId==null) return; if(!window.confirm('Delete this field model (and its fields)?')) return; setLoading(true); setError(null); try { await deleteFieldModel(selectedModelId); await loadModels(); setSelectedModelId(null); setFields([]);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveModel(){ setLoading(true); setError(null); try { const payload={ fieldModelName:modelForm.fieldModelName, fieldModelDescription:modelForm.fieldModelDescription, fieldModelType:modelForm.fieldModelType } as Omit<ApiFieldModel,'id'>; let id=selectedModelId; if(id==null){ const nm=await addFieldModel(payload); id=nm.id; await loadModels(); } else { await updateFieldModel(id,payload); await loadModels(); } setSelectedModelId(id!); setModelEditing(false); await loadFields(id!);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }

  // Map datatype display name (from backend) to code used in form
  function dataTypeNameToCode(name?: string): string | undefined {
    if (!name) return undefined;
    const n = name.toLowerCase();
    if (n === 'text') return 'X';
    if (n === 'decimal' || n === 'number' || n === 'numeric') return 'D';
    if (n === 'currency' || n === 'valuta') return 'V';
    if (n === 'datetime' || n === 'date' || n === 'date/time') return 'T';
    return undefined;
  }

  function handleSelectField(id:number){ setSelectedFieldId(id); setFieldEditing(false); const f=fields.find(f=>f.id===id); if(f) {
      // ensure we prefer the explicit code if present, otherwise map from returned datatype name
      const dt = (f.fieldDataType && String(f.fieldDataType).trim()) || dataTypeNameToCode((f as any).fieldDatatypeName);
      setFieldForm({ fieldName:f.fieldName, fieldDataType: dt || emptyField.fieldDataType, fieldLength:f.fieldLength, fieldRequired:f.fieldRequired, fieldTitle:f.fieldTitle||'', fieldOffset:f.fieldOffset||'', fieldShowInSearch:!!f.fieldShowInSearch, fieldShowInDataGrid:!!f.fieldShowInDataGrid, fieldIsUniqueFolderIndex:!!f.fieldIsUniqueFolderIndex, fieldIsUniqueDocumentIndex:!!f.fieldIsUniqueDocumentIndex, fieldControlOrderBy: Number((f as any).fieldControlOrderBy ?? 0) });
    }
  }
  function handleAddField(){ setSelectedFieldId(null); setFieldEditing(true); setFieldForm(emptyField); }
  function handleEditField(){ 
    if(selectedFieldId!=null) {
      const f = fields.find(x => x.id === selectedFieldId);
      if (f) {
        const dt = (f.fieldDataType && String(f.fieldDataType).trim()) || dataTypeNameToCode((f as any).fieldDatatypeName) || emptyField.fieldDataType;
        setFieldForm({ fieldName:f.fieldName, fieldDataType: dt, fieldLength:f.fieldLength, fieldRequired:f.fieldRequired, fieldTitle:f.fieldTitle||'', fieldOffset:f.fieldOffset||'', fieldShowInSearch:!!f.fieldShowInSearch, fieldShowInDataGrid:!!f.fieldShowInDataGrid, fieldIsUniqueFolderIndex:!!f.fieldIsUniqueFolderIndex, fieldIsUniqueDocumentIndex:!!f.fieldIsUniqueDocumentIndex, fieldControlOrderBy: Number((f as any).fieldControlOrderBy ?? 0) });
      }
      setFieldEditing(true);
    }
  }
  async function handleDeleteField(){ if(selectedFieldId==null) return; if(!window.confirm('Delete this field?')) return; setLoading(true); setError(null); try { await deleteField(selectedFieldId); await loadFields(selectedModelId!); setSelectedFieldId(null);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveField(){ 
    if(selectedModelId==null) return; 
    setLoading(true); 
    setError(null); 
    try { 
      // Use code directly as selected value for API
      const apiDataType = fieldForm.fieldDataType; 
      const payload={ 
        fieldModelId:selectedModelId, 
        fieldName:fieldForm.fieldName, 
        fieldDataType: apiDataType, 
        fieldLength:fieldForm.fieldLength, 
        fieldRequired:fieldForm.fieldRequired, 
        fieldTitle:fieldForm.fieldTitle, 
        fieldOffset:fieldForm.fieldOffset, 
        fieldShowInSearch:fieldForm.fieldShowInSearch, 
        fieldShowInDataGrid:fieldForm.fieldShowInDataGrid, 
        fieldIsUniqueFolderIndex:fieldForm.fieldIsUniqueFolderIndex, 
        fieldIsUniqueDocumentIndex:fieldForm.fieldIsUniqueDocumentIndex,
        fieldControlOrderBy: fieldForm.fieldControlOrderBy
      } as Omit<ApiField,'id'>; 
      if(selectedFieldId==null) await addField(payload); 
      else await updateField(selectedFieldId,payload); 
      await loadFields(selectedModelId); 
      setFieldEditing(false);
    } catch(e:any){ 
      setError(e.message);
    } finally { 
      setLoading(false);
    } 
  }

  function onModelFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>){ const {name,value}=e.target; setModelForm(p=>({...p,[name]:value})); }
  function onFieldFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>){ const tgt=e.target as HTMLInputElement; const {name,value,type,checked}=tgt; setFieldForm(p=>({...p,[name]: type==='checkbox'? checked: (name==='fieldLength' || name==='fieldControlOrderBy' ? Number(value): value) } as any)); }


  // Helper function to get display label for datatype codes
  function getDataTypeLabel(code: string): string {
    const option = dataTypeOptions.find(opt => opt.code === code);
    return option?.label || code;
  }

  const inputStyle: React.CSSProperties = { padding:'4px 6px', fontSize:14, borderRadius:4, border:'1px solid #cfd8dc', background:'#f7fafd', width:'100%' };
  const buttonPrimary: React.CSSProperties = { padding:'8px 16px', borderRadius:6, background:'#4e6e8e', color:'#fff', fontWeight:600, border:'none', cursor:'pointer' };
  const buttonSecondary: React.CSSProperties = { ...buttonPrimary, background:'#e0e6ed', color:'#123', fontWeight:500 };
  const buttonDanger: React.CSSProperties = { ...buttonPrimary, background:'#c62828' };

  const selectedModel=models.find(m=>m.id===selectedModelId)||null; 
  const selectedField=fields.find(f=>f.id===selectedFieldId)||null;

  // Sort fields by fieldControlOrderBy then by name
  const sortedFields = React.useMemo(() => {
    const getOrder = (f: any) => (typeof f.fieldControlOrderBy === 'number' ? f.fieldControlOrderBy : Number(f.fieldControlOrderBy ?? Number.MAX_SAFE_INTEGER));
    return [...fields].sort((a: any, b: any) => {
      const ao = getOrder(a);
      const bo = getOrder(b);
      if (ao !== bo) return (ao ?? Number.MAX_SAFE_INTEGER) - (bo ?? Number.MAX_SAFE_INTEGER);
      return (a.fieldName || '').localeCompare(b.fieldName || '');
    });
  }, [fields]);

  return (
    <div style={{ display:'flex', height:'calc(90vh / var(--ui-scale))', minHeight:'calc(90vh / var(--ui-scale))', border:'1px solid #ccc', borderRadius:12, overflow:'hidden' }}>
      <div style={{ width:380, borderRight:'1px solid #ccc', display:'flex', flexDirection:'column', background:'#f7fafd' }}>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #e0e6ed' }}>
          <h3 style={{ margin:0, fontWeight:600 }}>Field Models</h3>
          <button onClick={handleAddModel} title='Add field model' style={{ background:'#4e6e8e', border:'none', width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='12' y1='5' x2='12' y2='19'/>
              <line x1='5' y1='12' x2='19' y2='12'/>
            </svg>
          </button>
        </div>
        {loading && <div style={{ padding:'8px 16px' }}>Loading...</div>}
        {error && <div style={{ padding:'8px 16px', color:'red' }}>{error}</div>}
        <div style={{ overflowY:'auto', flex:1 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
            <thead>
              <tr style={{ background:'#eaf1f7' }}>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Name</th>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {models.map(m=>{ 
                const sel=m.id===selectedModelId; 
                return (
                  <tr key={m.id} onClick={()=>handleSelectModel(m.id)} style={{ cursor:'pointer', background: sel? '#d8ecff':'transparent' }}>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{m.fieldModelName}</td>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid "#f0f4f7' }}>{m.fieldModelType}</td>
                  </tr>
                ); 
              })}
              {models.length===0 && !loading && (
                <tr>
                  <td colSpan={2} style={{ padding:16, textAlign:'center', color:'#888' }}>No field models</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'16px 24px', overflow:'auto' }}>
        {modelEditing ? (
          <div style={{ maxWidth:900 }}>
            <h3 style={{ marginTop:0 }}>{selectedModelId==null? 'Add Field Model':'Edit Field Model'}</h3>
            <form onSubmit={e=>{ e.preventDefault(); handleSaveModel(); }} style={{ display:'grid', gridTemplateColumns:'170px 1fr 170px 1fr', gap:12, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <label style={{ alignSelf:'center', fontWeight:500 }}>Name</label>
              <input name='fieldModelName' value={modelForm.fieldModelName} onChange={onModelFormChange} required style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>Type</label>
              <select name='fieldModelType' value={modelForm.fieldModelType} onChange={onModelFormChange} required style={inputStyle}>
                <option value=''>Select type...</option>
                {modelTypeOptions.map(o=> <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label style={{ alignSelf:'center', fontWeight:500 }}>Description</label>
              <input name='fieldModelDescription' value={modelForm.fieldModelDescription} onChange={onModelFormChange} style={{ ...inputStyle, gridColumn:'span 3' }} />
              <div style={{ gridColumn:'1 / span 4', display:'flex', gap:12, marginTop:4 }}>
                <button type='submit' style={buttonPrimary} disabled={loading}>Save</button>
                <button type='button' style={buttonSecondary} onClick={()=>setModelEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedModel ? (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            <div style={{ maxWidth:1000 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ margin:0 }}>Field Model Details</h3>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={handleEditModel} style={buttonSecondary} disabled={loading}>Edit</button>
                  <button onClick={handleDeleteModel} style={buttonDanger} disabled={loading}>Delete</button>
                </div>
              </div>
              <div style={{ background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', display:'grid', gridTemplateColumns:'160px 1fr', rowGap:8 }}>
                <div style={{ fontWeight:500 }}>Name:</div>
                <div>{selectedModel.fieldModelName}</div>
                <div style={{ fontWeight:500 }}>Type:</div>
                <div>{selectedModel.fieldModelType}</div>
                <div style={{ fontWeight:500 }}>Description:</div>
                <div>{selectedModel.fieldModelDescription}</div>
              </div>
            </div>
            <div style={{ maxWidth:1400 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <h3 style={{ margin:0 }}>Fields</h3>
                <button onClick={handleAddField} style={buttonSecondary} disabled={loading}>Add Field</button>
              </div>
              <div style={{ border:'1px solid #e0e6ed', borderRadius:12, overflow:'hidden', background:'#fff', maxHeight:'400px' }}>
                <div style={{ overflowY:'auto', maxHeight:'400px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
                    <thead>
                      <tr style={{ background:'#eaf1f7', position:'sticky', top:0 }}>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Order</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Name</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Title</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Type</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Length</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Req</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Search</th>
                        <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Grid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFields.map((f:any)=>{ 
                        const sel=f.id===selectedFieldId; 
                        const isUnique = !!f.fieldIsUniqueFolderIndex || !!f.fieldIsUniqueDocumentIndex;
                        return (
                          <tr key={f.id} onClick={()=>handleSelectField(f.id)} style={{ cursor:'pointer', background: sel? '#d8ecff':'transparent' }}>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldControlOrderBy}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>
                              {f.fieldName}
                              {isUnique && <span title="Unique index" style={{ marginLeft: 6, color: '#b58800' }}>&#x1F511;</span>}
                            </td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldTitle}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldDatatypeName}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldLength}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldRequired? 'Yes':'No'}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldShowInSearch? 'Yes':'No'}</td>
                            <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7' }}>{f.fieldShowInDataGrid? 'Yes':'No'}</td>
                          </tr>
                        ); 
                      })}
                      {sortedFields.length===0 && (
                        <tr>
                          <td colSpan={8} style={{ padding:16, textAlign:'center', color:'#888' }}>No fields</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {fieldEditing ? (
                <form onSubmit={e=>{ e.preventDefault(); handleSaveField(); }} style={{ marginTop:16, display:'grid', gridTemplateColumns:'150px 1fr 150px 1fr', gap:12, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Name</label>
                  <input name='fieldName' value={fieldForm.fieldName} onChange={onFieldFormChange} required style={inputStyle} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Title</label>
                  <input name='fieldTitle' value={fieldForm.fieldTitle} onChange={onFieldFormChange} style={inputStyle} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Order</label>
                  <input name='fieldControlOrderBy' type='number' value={fieldForm.fieldControlOrderBy} onChange={onFieldFormChange} style={inputStyle} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Data Type</label>
                  <select name='fieldDataType' value={fieldForm.fieldDataType} onChange={onFieldFormChange} style={inputStyle}>
                    {dataTypeOptions.map(o=> <option key={o.code} value={o.code}>{o.label}</option>)}
                  </select>
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Length</label>
                  <input name='fieldLength' type='number' value={fieldForm.fieldLength} onChange={onFieldFormChange} style={inputStyle} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Offset</label>
                  <input name='fieldOffset' value={fieldForm.fieldOffset} onChange={onFieldFormChange} style={inputStyle} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Required</label>
                  <input type='checkbox' name='fieldRequired' checked={fieldForm.fieldRequired} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Show In Search</label>
                  <input type='checkbox' name='fieldShowInSearch' checked={fieldForm.fieldShowInSearch} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Show In Grid</label>
                  <input type='checkbox' name='fieldShowInDataGrid' checked={fieldForm.fieldShowInDataGrid} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Folder</label>
                  <input type='checkbox' name='fieldIsUniqueFolderIndex' checked={fieldForm.fieldIsUniqueFolderIndex} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Document</label>
                  <input type='checkbox' name='fieldIsUniqueDocumentIndex' checked={fieldForm.fieldIsUniqueDocumentIndex} onChange={onFieldFormChange} />
                  <div style={{ gridColumn:'1 / span 4', display:'flex', gap:12 }}>
                    <button type='submit' style={buttonPrimary} disabled={loading}>Save Field</button>
                    <button type='button' style={buttonSecondary} onClick={()=>setFieldEditing(false)} disabled={loading}>Cancel</button>
                  </div>
                </form>
              ) : selectedField ? (
                <div style={{ marginTop:16, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', display:'grid', gridTemplateColumns:'150px 1fr', rowGap:6 }}>
                  <div style={{ fontWeight:500 }}>Name:</div>
                  <div>{selectedField.fieldName}</div>
                  <div style={{ fontWeight:500 }}>Title:</div>
                  <div>{selectedField.fieldTitle}</div>
                  <div style={{ fontWeight:500 }}>Type:</div>
                  <div>{getDataTypeLabel((selectedField.fieldDataType as any) || dataTypeNameToCode((selectedField as any).fieldDatatypeName) || '')}</div>
                  <div style={{ fontWeight:500 }}>Order:</div>
                  <div>{(selectedField as any).fieldControlOrderBy}</div>
                  <div style={{ fontWeight:500 }}>Length:</div>
                  <div>{selectedField.fieldLength}</div>
                  <div style={{ fontWeight:500 }}>Required:</div>
                  <div>{selectedField.fieldRequired? 'Yes':'No'}</div>
                  <div style={{ fontWeight:500 }}>Search:</div>
                  <div>{selectedField.fieldShowInSearch? 'Yes':'No'}</div>
                  <div style={{ fontWeight:500 }}>Grid:</div>
                  <div>{selectedField.fieldShowInDataGrid? 'Yes':'No'}</div>
                  <div style={{ fontWeight:500 }}>Unique Folder:</div>
                  <div>{selectedField.fieldIsUniqueFolderIndex? 'Yes':'No'}</div>
                  <div style={{ fontWeight:500 }}>Unique Document:</div>
                  <div>{selectedField.fieldIsUniqueDocumentIndex? 'Yes':'No'}</div>
                  <div style={{ gridColumn:'1 / span 2', display:'flex', gap:8, marginTop:8 }}>
                    <button onClick={handleEditField} style={buttonSecondary} disabled={loading}>Edit Field</button>
                    <button onClick={handleDeleteField} style={buttonDanger} disabled={loading}>Delete Field</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop:16, color:'#567' }}>Select a field to view details or click 'Add Field'.</div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ color:'#567', fontSize:16, paddingTop:12 }}>Select a field model or click the + icon to add a new field model.</div>
        )}
      </div>
    </div>
  );
}
